import { memo, useEffect, useMemo, useRef } from 'react';
import { Handle, Position, NodeProps, useReactFlow, useUpdateNodeInternals, Connection } from 'reactflow';
import { NodeResizer } from "@reactflow/node-resizer";
import "@reactflow/node-resizer/dist/style.css";
import { Smartphone } from 'lucide-react';
import { SourceNodeData } from '../../types';
import { useUIContext } from '../../providers/UIProvider';
import { calculateHandleStyle, groupHandlesByPosition, NODE_DIMENSIONS } from '../../utils/nodeHelpers';
import { useNodeResizing } from '../../hooks/useNodeResizing';

function MobileSDKNode({
  data,
  selected,
  id
}: NodeProps<SourceNodeData>) {
  const { isDarkMode } = useUIContext();
  const updateNodeInternals = useUpdateNodeInternals();
  const reactFlowInstance = useReactFlow();
  const nodeContentRef = useRef<HTMLDivElement>(null);
  
  // Calculate minimum height based on content
  const calculateMinHeight = useMemo(() => {
    return (width: number, headerHeight: number) => {
      // Start with vertical padding to ensure content doesn't touch borders
      let calculatedHeight = NODE_DIMENSIONS.VERTICAL_PADDING;
      
      // Add header height (measured or estimated)
      calculatedHeight += headerHeight || NODE_DIMENSIONS.HEADER_CONTENT_ESTIMATED_HEIGHT;
      
      const showLabels = width >= NODE_DIMENSIONS.HIDE_LABELS_WIDTH_THRESHOLD;
  
      if (showLabels) {
        let visibleLabelCount = 0;
        if (data.eventVolume && data.eventVolumeVisibility !== "hidden") visibleLabelCount++;
        if (data.eventSpec && data.eventSpecVisibility !== "hidden") visibleLabelCount++;
        if (data.otherConsentToolName && data.consentToolVisibility !== "hidden") visibleLabelCount++;
  
        if (visibleLabelCount > 0) {
          calculatedHeight += NODE_DIMENSIONS.LABEL_ROW_ESTIMATED_HEIGHT; // Base for one row
        }
        // Simple heuristic: if more than 2 labels and width is tight, assume a second row for labels
        if (visibleLabelCount > 2 && width < (NODE_DIMENSIONS.MIN_WIDTH + 60)) { 
          calculatedHeight += NODE_DIMENSIONS.LABEL_ROW_ESTIMATED_HEIGHT;
        }
      }
  
      const notesExist = data.notes && data.notes.trim() !== "" && data.notesVisibility !== "hidden";
      if (notesExist) {
        // If notes could be shown, add their minimum required space.
        calculatedHeight += NODE_DIMENSIONS.NOTES_MIN_VISIBLE_HEIGHT_ADDITION;
      }
      
      return Math.min(calculatedHeight, NODE_DIMENSIONS.MAX_HEIGHT);
    };
  }, [data]);

  // Use the node resizing hook
  const { 
    onResize, 
    onResizeEnd, 
    currentWidth, 
    currentHeight,
    effectiveMinHeight 
  } = useNodeResizing({
    id,
    nodeType: 'mobile-sdk',
    minWidth: NODE_DIMENSIONS.MIN_WIDTH,
    maxWidth: NODE_DIMENSIONS.MAX_WIDTH,
    minHeight: NODE_DIMENSIONS.MIN_HEIGHT,
    maxHeight: NODE_DIMENSIONS.MAX_HEIGHT,
    contentRef: nodeContentRef,
    calculateMinHeight
  });

  // Effect to update node internals when handles change
  useEffect(() => {
    if (Array.isArray(data.handles)) {
      updateNodeInternals(id);
    }
  }, [id, data.handles, updateNodeInternals]);

  return (
    <div
      style={{
        width: currentWidth,
        height: currentHeight,
        position: 'relative',
      }}
      className="node-wrapper"
    >
      {selected && (
        <NodeResizer
          minWidth={NODE_DIMENSIONS.MIN_WIDTH}
          maxWidth={NODE_DIMENSIONS.MAX_WIDTH}
          minHeight={effectiveMinHeight}
          maxHeight={NODE_DIMENSIONS.MAX_HEIGHT}
          isVisible={selected}
          onResize={onResize}
          onResizeEnd={onResizeEnd}
          handleStyle={{
            width: '8px',
            height: '8px',
            backgroundColor: '#3b82f6', // blue-500
            border: '1px solid white',
            borderRadius: '2px',
          }}
          lineStyle={{
            borderColor: '#3b82f6',
            borderWidth: '1px',
          }}
        />
      )}
      
      <div
        className={`
          w-full h-full pl-3 pr-4 py-2 rounded-lg bg-white dark:bg-gray-700 border transition-all cursor-pointer shadow-sm dark:shadow-sm-dark hover:shadow-md dark:hover:shadow-md-dark overflow-hidden
          flex flex-col justify-center
          ${selected 
            ? "border-blue-500 shadow-blue-100 outline outline-4 outline-blue-500/20 outline-offset-0" 
            : "border-gray-300 dark:border-gray-600"
          }
        `}
      >
        <div ref={nodeContentRef} className="flex items-center justify-center gap-2 node-content">
          <div
            className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
              isDarkMode ? 'bg-blue-100 text-blue-600' : 'bg-blue-50 text-blue-600'
            }`}
          >
            <Smartphone className="h-4 w-4" />
          </div>
          <div className="min-w-0 content-container">
            <h3 className="font-medium text-sm text-gray-900 dark:text-gray-100 truncate">
              {data.label}
            </h3>
            <div className="labels-container flex flex-wrap gap-x-1">
              {/* Event Volume - only show if visibility is not hidden */}
              {data.eventVolume && data.eventVolumeVisibility !== "hidden" && (
                <span
                  className="mt-1 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium truncate bg-gray-100 text-gray-800"
                >
                  {data.eventVolume}
                </span>
              )}
              
              {/* Platform - only show if visibility is not hidden and it's not empty */}
              {data.eventSpec && data.eventSpecVisibility !== "hidden" && (
                <span
                  className="mt-1 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium truncate bg-blue-100 text-blue-800"
                >
                  {data.eventSpec}
                </span>
              )}
              
              {/* Custom Label - only show if visibility is not hidden */}
              {data.otherConsentToolName && data.consentToolVisibility !== "hidden" && (
                <span
                  className="mt-1 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium truncate bg-yellow-100 text-yellow-800"
                >
                  {data.otherConsentToolName}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Notes - only show if visibility is not hidden and we have notes */}
        {currentHeight >= NODE_DIMENSIONS.HIDE_NOTES_HEIGHT_THRESHOLD && data.notes && data.notes.trim() !== "" && data.notesVisibility !== "hidden" && (
          <div className="mt-2 text-[8px] text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-800/50 px-2 py-1 rounded max-h-[80px] overflow-y-auto whitespace-pre-line">
            {data.notes}
          </div>
        )}

        {/* --- Handle Rendering --- */}
        {useMemo(() => {
          const handles = Array.isArray(data.handles) ? [...data.handles] : [];
          const handlesByPosition = groupHandlesByPosition(handles);

          if (handles && handles.length > 0) {
            return Object.entries(handlesByPosition).map(([_, positionHandles]) =>
              positionHandles.map((handle, index) => (
                <Handle
                  key={`${handle.id}-${handle.color}`}
                  id={handle.id}
                  type={handle.type}
                  position={handle.position as Position}
                  className={`!w-3 !h-3 !border-2 !border-white dark:!border-gray-700`}
                  style={{
                    ...calculateHandleStyle(index, positionHandles.length, handle.position),
                    backgroundColor: handle.color || (handle.type === 'source' ? '#3B82F6' : '#6366F1'),
                  }}
                  isValidConnection={handle.type === 'target' ? (connection: Connection) => {
                    // Keep the specific validation logic for target handles
                    if (!connection.source) return false;
                    const connectingNode = reactFlowInstance.getNode(connection.source);
                    return connectingNode?.type === 'sync-injector';
                  } : undefined}
                />
              ))
            );
          } else {
            return null;
          }
        }, [data.handles, reactFlowInstance])}
        {/* --- End Handle Rendering --- */}
      </div>
    </div>
  );
}

export default memo(MobileSDKNode);
